import type { ConsentState } from './consent';

declare global {
  interface Window {
    __spConsent?: ConsentState;
    posthog?: any;
  }
}

type ScriptSpec = {
  id: string;
  src: string;
  async?: boolean;
  defer?: boolean;
  crossOrigin?: string;
};

function loadScriptOnce(spec: ScriptSpec): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const existing = document.getElementById(spec.id) as HTMLScriptElement | null;
      if (existing) {
        resolve();
        return;
      }

      const s = document.createElement('script');
      s.id = spec.id;
      s.type = 'text/javascript';
      s.async = spec.async !== false;
      if (spec.defer) s.defer = true;
      if (spec.crossOrigin) s.crossOrigin = spec.crossOrigin;
      s.src = spec.src;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load script: ${spec.src}`));

      document.head.appendChild(s);
    } catch (e) {
      reject(e);
    }
  });
}

function deleteCookieByName(name: string): void {
  try {
    const base = `${encodeURIComponent(name)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
    document.cookie = base;
    // Best-effort: domini comuni
    document.cookie = `${base}; Domain=sostegnopro.it`;
    document.cookie = `${base}; Domain=.sostegnopro.it`;
  } catch {
    // ignore
  }
}

function deleteCookiesByPrefix(prefixes: string[]): void {
  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    for (const c of cookies) {
      const eq = c.indexOf('=');
      const rawName = (eq >= 0 ? c.slice(0, eq) : c).trim();
      const name = decodeURIComponent(rawName);
      if (prefixes.some((p) => name.startsWith(p))) {
        deleteCookieByName(name);
      }
    }
  } catch {
    // ignore
  }
}

function ensurePosthogStub(): void {
  if (window.posthog) return;
  const stub: any[] = [];
  const api: any = stub;
  api._i = [];
  const methods =
    'init capture identify register register_once unregister reset opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing'.split(
      ' '
    );
  for (const m of methods) {
    (api as any)[m] = function (...args: any[]) {
      (api as any).push([m, ...args]);
    };
  }
  window.posthog = api;
}

async function initPosthogIfConfigured(): Promise<void> {
  const key = (process.env.REACT_APP_POSTHOG_KEY || '').trim() || 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
  const apiHost = (process.env.REACT_APP_POSTHOG_HOST || '').trim() || 'https://us.i.posthog.com';
  if (!key) return;

  // Se PostHog è già inizializzato, non reinizializzare.
  if ((window.posthog as any)?.__loaded) return;

  ensurePosthogStub();

  // PostHog cloud: lo snippet raccomanda di caricare da host "-assets".
  const assetsHost = apiHost.replace('.i.posthog.com', '-assets.i.posthog.com');
  const src = `${assetsHost}/static/array.js`;

  await loadScriptOnce({
    id: 'sp-posthog',
    src,
    async: true,
    crossOrigin: 'anonymous'
  });

  try {
    if (typeof window.posthog?.init === 'function') {
      window.posthog.init(key, {
        api_host: apiHost,
        person_profiles: 'identified_only'
      });
      (window.posthog as any).__loaded = true;
    }
  } catch {
    // ignore
  }
}

function disablePosthogBestEffort(): void {
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset();
    }
  } catch {
    // ignore
  }

  // Best-effort cleanup cookies PostHog
  deleteCookiesByPrefix(['ph_', 'posthog']);
}

/**
 * Applica il consenso:
 * - Prima del consenso: non carica nulla di facoltativo.
 * - Dopo il consenso: carica dinamicamente gli script consentiti.
 * - Revoca: best-effort (opt-out + rimozione cookie noti).
 *
 * Necessari: sempre attivi (app/auth). Facoltativi: analytics/marketing/functional.
 */
export async function applyConsent(consent: ConsentState): Promise<void> {
  window.__spConsent = consent;

  // ANALYTICS (facoltativi)
  if (consent.analytics) {
    await initPosthogIfConfigured();
  } else {
    disablePosthogBestEffort();
  }

  // MARKETING (facoltativi)
  // Al momento non risultano integrazioni marketing nel progetto. Se aggiunte (es. Meta Pixel),
  // vanno caricate qui *solo* dopo consenso esplicito.

  // FUNCTIONAL (facoltativi)
  // Esempi futuri: chat esterne, mappe embed, widget di terze parti.
}
